import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import Moment from 'moment';
import {
  ReturnTimeslotIneligibilityReason,
  ReturnTimeslotPurchaseResponse,
  ReturnTimeslotView,
  ReturnTimeslotViewResponse,
  TimeSlotValue,
} from '../models/APIModels';
import { axiosKK } from '../services/networkRequest';
import { toMoney } from '../utilities/MoneyConversion';
import Button from './Button';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);

type PickerState = 'loading' | 'idle' | 'submitting' | 'confirming_3ds' | 'error';

interface ReturnTimeslotPickerProps {
  orderId: number;
  deliveryDate?: number | string | null;
  /** Fires after a slot is successfully booked, so the parent can refresh. */
  onBooked?: (slot: TimeSlotValue) => void;
  /**
   * Eligibility the caller already holds, used for first paint.
   *
   * The dashboard has this from `/order/return-timeslots/eligible`, and the
   * per-order re-read costs seconds against the live database — long enough that
   * the modal opened blank. Painting from it is safe: the re-read still runs and
   * overwrites this, and the purchase endpoint re-checks server-side regardless,
   * so an optimistic render can never sell a slot that has lapsed.
   */
  initialView?: ReturnTimeslotView;
}

const SLOT_LABEL: Record<TimeSlotValue, string> = {
  pre_12: 'Pre-12',
  post_12: 'Post-12',
};

const SLOT_DESCRIPTION: Record<TimeSlotValue, string> = {
  pre_12: 'Delivery before 12pm',
  post_12: 'Delivery after 12pm',
};

/**
 * Reasons worth explaining. Everything else collapses to the generic
 * "no longer available" message from the brief — the customer does not need to know
 * which internal condition failed, and some of them (courier, warehouse) would leak
 * operational detail.
 */
const REASON_COPY: Partial<Record<ReturnTimeslotIneligibilityReason, string>> = {
  TOO_EARLY:
    'Timed delivery opens closer to your delivery date. We will email you as soon as it is available.',
  TOO_LATE:
    'This timeslot is no longer available for your delivery. Please contact us if you need help with your delivery time.',
};

const GENERIC_UNAVAILABLE =
  'This timeslot is no longer available for your delivery. Please contact us if you need help with your delivery time.';

/**
 * Money committed but not settled. Mirrors ReturnTimeslot.PENDING_STATUSES on the
 * backend — shown as "confirming", never as booked, and never as available.
 */
const PENDING_PAYMENT_STATUSES = ['requires_action', 'pending'];

/**
 * Pre-12 / Post-12 picker for a return delivery.
 *
 * Renders exactly what `GET /order/{id}/return-timeslot` returns and decides nothing
 * itself. Eligibility depends on the warehouse, the assigned return courier and an
 * Ops-editable matrix, none of which the browser can see — so unlike the collection
 * modal, there is deliberately no client-side working-day filter here.
 */
export function ReturnTimeslotPicker({
  orderId,
  deliveryDate,
  onBooked,
  initialView,
}: ReturnTimeslotPickerProps): JSX.Element | null {
  const [view, setView] = useState<ReturnTimeslotView | null>(initialView ?? null);
  const [state, setState] = useState<PickerState>(initialView ? 'idle' : 'loading');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlotValue | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const mountedRef = useRef(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadView = useCallback(async () => {
    try {
      const { data } = await axiosKK.get<ReturnTimeslotViewResponse>(
        `/order/${orderId}/return-timeslot`,
      );
      if (!mountedRef.current) return;
      setView(data.returnTimeslot);
      setState('idle');
    } catch {
      // A failure here must not break the order page. On the first load there is
      // nothing to show and `view` is already null, so the section stays hidden.
      //
      // Deliberately does NOT null an existing view: this same call re-reads after a
      // purchase, and blanking it on a transient network error would erase the
      // confirmation of a booking the customer has just paid for.
      if (!mountedRef.current) return;
      setState('idle');
    }
  }, [orderId]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  // The availability email links to /order/{id}?timeslot=1. Bring the picker into
  // view so the customer lands on the thing they clicked through for.
  useEffect(() => {
    if (state !== 'idle' || !view?.eligible) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('timeslot') !== '1') return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [state, view]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;

    setState('submitting');
    setErrorMessage('');

    try {
      const { data } = await axiosKK.post<ReturnTimeslotPurchaseResponse>(
        '/order/return-timeslot',
        { orderId, timeSlot: selectedSlot },
      );

      if (data.paymentIntentClientSecret) {
        if (mountedRef.current) setState('confirming_3ds');
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Payment gateway not available');

        const { error } = await stripe.confirmCardPayment(
          data.paymentIntentClientSecret,
        );

        if (error) {
          if (mountedRef.current) {
            setState('error');
            setErrorMessage(error.message || 'Card authentication failed.');
          }
          return;
        }
      }

      if (!mountedRef.current) return;
      onBooked?.(selectedSlot);
      await loadView();
    } catch (err: any) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      const message = err?.response?.data?.message;

      setState('error');

      if (status === 400 && message?.includes('No payment method')) {
        setErrorMessage(
          'Please add a payment method to your account before booking a timeslot.',
        );
        return;
      }

      if (status === 409) {
        // Eligibility lapsed between the page loading and the click. Re-read so the
        // customer sees the current truth rather than a stale form.
        setErrorMessage(GENERIC_UNAVAILABLE);
        loadView();
        return;
      }

      setErrorMessage(message || 'Something went wrong. Please try again.');
    }
  };

  // Inline on the order page there is nothing to hold open, so stay silent. In a
  // modal the caller passes initialView, so this branch is not reached and the
  // customer never sees an empty dialog.
  if (state === 'loading' || !view) return null;

  // ---- Paid, but Stripe has not settled it yet ----
  // After a 3DS challenge the row stays `requires_action` until the webhook lands
  // seconds later. Without this branch the customer who had just authenticated was
  // told the slot was no longer available.
  if (view.selected && PENDING_PAYMENT_STATUSES.includes(view.selected.paymentStatus)) {
    return (
      <div className="return-timeslot return-timeslot--booked" ref={sectionRef}>
        <h3 className="return-timeslot__heading">Your delivery timeslot</h3>
        <p className="return-timeslot__booked-slot">
          {SLOT_LABEL[view.selected.slot]} delivery
        </p>
        <p className="return-timeslot__booked-detail">
          We are confirming your payment. This usually takes a few seconds — refresh
          the page shortly and your timeslot will be shown as booked.
        </p>
      </div>
    );
  }

  // ---- Booked ----
  if (view.selected) {
    const { slot, feePence } = view.selected;
    return (
      <div className="return-timeslot return-timeslot--booked" ref={sectionRef}>
        <h3 className="return-timeslot__heading">Your delivery timeslot</h3>
        <p className="return-timeslot__booked-slot">
          <span className="return-timeslot__tick" aria-hidden="true">
            &#10003;
          </span>
          {SLOT_LABEL[slot]} delivery
          {deliveryDate
            ? ` — ${Moment(deliveryDate).format('dddd D MMMM YYYY')}`
            : ''}
        </p>
        <p className="return-timeslot__booked-detail">
          {SLOT_DESCRIPTION[slot]}
          {feePence > 0 ? ` · ${toMoney(feePence)} paid` : ' · Included'}
        </p>
      </div>
    );
  }

  // ---- Not eligible ----
  if (!view.eligible) {
    // Silent for the cases where there is simply nothing to offer — no delivery
    // booked yet, or this area is not covered. Showing a "not available" notice for
    // those would confuse far more customers than it helps.
    const quietReasons: ReturnTimeslotIneligibilityReason[] = [
      'NO_DELIVERY_DATE',
      'ORDER_CANCELLED',
      'AREA_NOT_ELIGIBLE',
      'COURIER_NOT_ELIGIBLE',
      'NATIONAL_CARRIER',
      'NO_SLOTS_ENABLED',
    ];
    if (!view.reason || quietReasons.includes(view.reason)) return null;

    return (
      <div className="return-timeslot return-timeslot--unavailable" ref={sectionRef}>
        <h3 className="return-timeslot__heading">Delivery timeslot</h3>
        <p className="return-timeslot__notice">
          {REASON_COPY[view.reason] || GENERIC_UNAVAILABLE}
        </p>
      </div>
    );
  }

  // ---- Eligible ----
  const busy = state === 'submitting' || state === 'confirming_3ds';

  return (
    <div className="return-timeslot" ref={sectionRef}>
      <h3 className="return-timeslot__heading">Choose your delivery timeslot</h3>
      <p className="return-timeslot__intro">
        We can offer timed delivery
        {view.areaLabel ? ` in ${view.areaLabel}` : ''}
        {deliveryDate
          ? ` on ${Moment(deliveryDate).format('dddd D MMMM YYYY')}`
          : ''}
        .
      </p>

      <div className="return-timeslot__options">
        {view.options.map((option) => (
          <label
            key={option.slot}
            className={`return-timeslot__option${
              selectedSlot === option.slot
                ? ' return-timeslot__option--selected'
                : ''
            }`}
          >
            <input
              type="radio"
              name="return-timeslot"
              value={option.slot}
              checked={selectedSlot === option.slot}
              disabled={busy}
              onChange={() => setSelectedSlot(option.slot)}
            />
            <span className="return-timeslot__option-body">
              <span className="return-timeslot__option-name">
                {SLOT_LABEL[option.slot]}
              </span>
              <span className="return-timeslot__option-detail">
                {SLOT_DESCRIPTION[option.slot]}
              </span>
              <span className="return-timeslot__option-price">
                {option.feePence > 0 ? `+${toMoney(option.feePence)}` : 'Included'}
              </span>
            </span>
          </label>
        ))}
      </div>

      {state === 'error' && errorMessage && (
        <div className="return-timeslot__error" role="alert">
          {errorMessage}
        </div>
      )}

      {state === 'confirming_3ds' && (
        <p className="return-timeslot__intro">Completing card authentication…</p>
      )}

      <Button
        variant="primary"
        // Disabled while in flight — the backend has its own duplicate guards, but
        // there is no reason to let a double-click get that far.
        disabled={!selectedSlot || busy}
        onClick={handleSubmit}
      >
        {busy ? 'Processing…' : 'Confirm my delivery timeslot'}
      </Button>
    </div>
  );
}

export default ReturnTimeslotPicker;
